/**
 * XYZ data auto-detection — delimiter, header row, and column roles
 * for tabular geophysical data (CSV/TXT/DAT exports).
 *
 * A UTM zone is taken from a stated EPSG / "UTM 16N" or from longitude and
 * latitude in the file. It is never defaulted to zone 17: those numbers are a
 * different place. Mirrors backend/xyz_pipeline.py.
 */
import { resolveUtmZone } from './coordinates';

const LON_HEADER = /(?:^|[^a-z])(lon(gitude)?|long)([^a-z]|$)/i;
const LAT_HEADER = /(?:^|[^a-z])(lat(itude)?)([^a-z]|$)/i;

function columnFloats(rows, col) {
  const out = [];
  for (const row of rows) {
    const v = parseFloat(row?.[col]);
    if (Number.isFinite(v)) out.push(v);
  }
  return out;
}

/**
 * UTM zone for a table, from a header/filename (`extraText`) or from longitude
 * and latitude columns that are not the easting/northing pair. Null when the
 * file does not say where it is.
 */
export function resolveUtmFromTable(headers, rows, xIndex, yIndex, extraText = '') {
  const sample = (rows || []).slice(0, 200);
  const names = headers || [];
  const texts = [extraText, names.join(' '), ...names];
  let lons = [];
  let lats = [];
  names.forEach((h, i) => {
    if (i === xIndex || i === yIndex) return;
    if (LON_HEADER.test(h || '')) lons = columnFloats(sample, i);
    else if (LAT_HEADER.test(h || '')) lats = columnFloats(sample, i);
  });
  return resolveUtmZone({ texts, lons, lats });
}

// Header keyword patterns, strongest first
// (tested against normaliseHeader() output: lower-case ASCII, units stripped, '_' separators)
const X_PATTERNS = [
  /^(lon|long|longitude|lng)$/i, /^(x|e|east|easting)$/i, /^utm_?e(ast)?(ing)?$/i,
  /lon(gitude)?/i, /east/i, /(^|_)x($|_)/i,
];
const Y_PATTERNS = [
  /^(lat|latitude)$/i, /^(y|n|north|northing)$/i, /^utm_?n(orth)?(ing)?$/i,
  /lat(itude)?/i, /north/i, /(^|_)y($|_)/i,
];
const GEO_NAME = /^(lon|long|longitude|lng|lat|latitude)$/;

/** Numeric share a column needs to be a coordinate/Z candidate; named X/Y columns get some slack (V.44). */
const NUMERIC_MIN = 0.8;
const NAMED_COORD_NUMERIC_MIN = 0.75;

/**
 * Header name for matching: BOM / zero-width / NBSP removed, accents folded, units in
 * brackets dropped ('Easting (m)' -> 'easting'), separators collapsed to '_'.
 */
export function normaliseHeader(h) {
  return String(h ?? '')
    .replace(/[\uFEFF\u200B-\u200D\u00A0]/g, ' ')
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\(.*?\)|\[.*?\]/g, ' ')
    .trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}
const Z_PATTERNS = [
  /resist|rho|ohm/i, /charge|(^|_)ip($|_)/i, /conduct/i, /(^|_)mag/i,
  /elev(ation)?/i, /depth/i, /altitude|alt/i, /(^|_)z($|_)/i, /value|measurement|reading/i,
  /velocity|amplitude|freq/i,
];

/**
 * Detect the delimiter that yields the most consistent column count.
 * @param {string} text - raw file text
 * @returns {string} ',' | '\t' | ';' | ' '
 */
export function detectDelimiter(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim()).slice(0, 25);
  if (lines.length === 0) return ',';

  const candidates = [',', '\t', ';', ' '];
  let best = ',', bestScore = -1;

  candidates.forEach(delim => {
    const counts = lines.map(l => splitLine(l, delim).length);
    const first = counts[0];
    if (first < 2) return; // must produce at least 2 columns
    const consistent = counts.filter(c => c === first).length / counts.length;
    // Prefer more columns when equally consistent
    const score = consistent * 100 + first;
    if (score > bestScore) { bestScore = score; best = delim; }
  });
  return best;
}

/**
 * Split a line, collapsing repeated spaces when the delimiter is a space. Double-quoted
 * fields may contain the delimiter ("Easting, m") and escaped quotes ("").
 */
export function splitLine(line, delimiter) {
  if (delimiter === ' ') return line.trim().split(/\s+/);
  if (!line.includes('"')) return line.split(delimiter).map(v => v.trim());
  const out = [];
  let cur = '', inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQ = false;
      else cur += ch;
    } else if (ch === '"' && cur.trim() === '') { inQ = true; cur = ''; }
    else if (ch === delimiter) { out.push(cur.trim()); cur = ''; }
    else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

/**
 * Find the header row index — the first row with non-numeric content.
 * Returns -1 if the file appears to be headerless (all numeric).
 * @param {string[][]} rows - parsed rows
 */
export function detectHeaderRow(rows) {
  for (let i = 0; i < Math.min(rows.length, 10); i++) {
    const row = rows[i];
    if (!row || row.length < 2) continue;
    const numericCount = row.filter(v => v !== '' && !isNaN(parseFloat(v)) && isFinite(v)).length;
    if (numericCount < row.length / 2) return i; // mostly non-numeric → header
  }
  return -1;
}

function matchScore(header, patterns) {
  for (let i = 0; i < patterns.length; i++) {
    if (patterns[i].test(header)) return 1 - i / (patterns.length * 2); // earlier pattern = stronger
  }
  return 0;
}

function columnStats(rows, col) {
  let min = Infinity, max = -Infinity, numeric = 0, total = 0;
  for (const row of rows) {
    const raw = row[col];
    if (raw === undefined || raw === '') continue;
    total++;
    const v = parseFloat(raw);
    if (isNaN(v) || !isFinite(v)) continue;
    numeric++;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return { min, max, numericRatio: total ? numeric / total : 0, total };
}

/**
 * Detect X/Y/Z column roles from headers + value ranges.
 * @param {string[][]} rows - data rows (header row removed)
 * @param {string[]} headers - column headers (may be synthesized col_0…)
 * @param {string} [text] - filename or preamble that may name a zone or EPSG code
 * @returns {{ x: number, y: number, z: number[], crs: string|null, projected: boolean, confidence: number, notes: string[] }}
 */
export function detectColumns(rows, headers, text = '') {
  const sample = rows.slice(0, 200);
  const nCols = headers.length;
  const names = headers.map(normaliseHeader);
  const named = names.map(n => matchScore(n, X_PATTERNS) >= 0.75 || matchScore(n, Y_PATTERNS) >= 0.75);
  const minRatio = (i) => (named[i] ? NAMED_COORD_NUMERIC_MIN : NUMERIC_MIN);
  const stats = headers.map((_, i) => columnStats(sample, i));
  const notes = [];

  // Range-based classification per column
  const rangeType = stats.map((s, i) => {
    if (s.numericRatio < minRatio(i)) return 'text';
    if (s.min >= -180 && s.max <= 180 && s.min >= -90 && s.max <= 90) return 'latlike'; // could be lat or lon
    if (s.min >= -180 && s.max <= 180) return 'lonlike';
    if (s.min >= 100000 && s.max <= 900000) return 'utm-easting';
    if (s.min >= 0 && s.max <= 10000000 && s.min > 900000) return 'utm-northing';
    return 'numeric';
  });

  // Two-pass: score every column for both roles, then assign jointly
  // (a greedy single pass lets a weak X score claim the latitude column
  //  and block its own — stronger — Y assignment)
  const sxList = [], syList = [];
  // Paired UTM evidence beats generic small-value ranges
  const hasE = rangeType.includes('utm-easting');
  const hasN = rangeType.includes('utm-northing');
  names.forEach((h, i) => {
    if (stats[i].numericRatio < minRatio(i)) { sxList.push(0); syList.push(0); return; }
    const rx = rangeType[i] === 'lonlike' ? 0.4 : rangeType[i] === 'latlike' ? 0.25 : rangeType[i] === 'utm-easting' ? (hasN ? 0.45 : 0.35) : 0;
    const ry = rangeType[i] === 'latlike' ? 0.4 : rangeType[i] === 'utm-northing' ? (hasE ? 0.45 : 0.35) : 0;
    sxList.push(matchScore(h, X_PATTERNS) * 0.6 + rx);
    syList.push(matchScore(h, Y_PATTERNS) * 0.6 + ry);
  });

  const argmax = (scores, exclude = -1) => {
    let best = -1, bestS = 0;
    scores.forEach((s, i) => { if (i !== exclude && s > bestS) { best = i; bestS = s; } });
    return [best, bestS];
  };

  let [x, xScore] = argmax(sxList);
  let [y, yScore] = argmax(syList);
  if (x === y && x !== -1) {
    // Same column won both roles — keep the stronger, re-pick the other
    if (xScore >= yScore) [y, yScore] = argmax(syList, x);
    else [x, xScore] = argmax(sxList, y);
  }

  // Headerless numeric files: conventional order X Y Z
  if (x === -1 && y === -1 && nCols >= 2 && stats[0].numericRatio > 0.8 && stats[1].numericRatio > 0.8) {
    x = 0; y = 1; xScore = 0.3; yScore = 0.3;
    notes.push('No coordinate headers found — assuming column order X, Y, Z');
  }

  // Z candidates: numeric columns that aren't x/y, keyword matches first
  const z = [];
  names.forEach((h, i) => {
    if (i === x || i === y || stats[i].numericRatio < NUMERIC_MIN) return;
    if (Z_PATTERNS.some(p => p.test(h))) z.unshift(i);
    else z.push(i);
  });

  // CRS guess. Projected metres do not include a UTM zone, so an unknown zone
  // stays unset. The previous EPSG:32617 default put zone-16 Chicago in Ohio.
  let crs = 'EPSG:4326';
  let projected = false;
  let crsConfident = false;
  if (x !== -1 && y !== -1) {
    const outside = stats[x].max > 180 || stats[x].min < -180 || stats[y].max > 90 || stats[y].min < -90;
    const isUTM = rangeType[x] === 'utm-easting' || rangeType[y] === 'utm-northing' || outside;
    const geoNamed = GEO_NAME.test(names[x]) && GEO_NAME.test(names[y]);
    const small = Math.max(Math.abs(stats[x].min), Math.abs(stats[x].max), Math.abs(stats[y].min), Math.abs(stats[y].max)) < 1000;
    if (isUTM && geoNamed && small) {
      // V.43: lat/lon columns with a few bad values stay WGS84 so those rows are rejected, not reprojected
      notes.push('Some latitude/longitude values are outside ±90°/±180° — those rows will be rejected');
    } else if (isUTM) {
      projected = true;
      const hit = resolveUtmFromTable(headers, sample, x, y, text);
      if (hit) {
        crs = hit.code;
        crsConfident = true;
        const where = hit.source === 'location'
          ? 'the longitude and latitude in the file'
          : 'the file header';
        notes.push(`UTM zone ${hit.zone}${hit.north ? 'N' : 'S'} from ${where}`);
      } else {
        crs = null;
        notes.push('Projected coordinates detected. The values do not say which CRS they are in — choose the UTM zone or an EPSG code. Nothing is assumed.');
      }
    } else {
      crsConfident = true;
    }
  }

  // Overall confidence
  let confidence = 0;
  if (x !== -1 && y !== -1) {
    confidence = Math.min(1, (xScore + yScore) / 1.6);
    if (z.length > 0) confidence = Math.min(1, confidence + 0.1);
    if (!crsConfident) confidence = Math.min(confidence, 0.7); // never auto-accept a guessed UTM zone
  }

  return {
    x, y, z,
    crs,
    projected,
    confidence: Math.round(confidence * 100) / 100,
    xScore: Math.round(xScore * 100) / 100,
    yScore: Math.round(yScore * 100) / 100,
    notes,
  };
}

/**
 * Parse DMS (degrees-minutes-seconds) strings like `30°15'20.5"N` or `-81 24 36` to decimal degrees.
 * Returns NaN if unparseable.
 */
export function dmsToDecimal(str) {
  if (typeof str === 'number') return str;
  const s = String(str).trim();
  const plain = parseFloat(s);
  if (!isNaN(plain) && /^-?[\d.]+$/.test(s)) return plain;

  const m = s.match(/^(-?)(\d{1,3})[°\s:]+(\d{1,2})['\s:]+([\d.]+)"?\s*([NSEW]?)$/i)
    || s.match(/^(-?)(\d{1,3})[°\s:]+([\d.]+)['\s:]*\s*([NSEW]?)$/i);
  if (!m) return NaN;

  let deg, minutes, seconds = 0, hemi;
  if (m.length === 6) { [, , deg, minutes, seconds, hemi] = m; }
  else { [, , deg, minutes, hemi] = m; }

  let dec = parseFloat(deg) + parseFloat(minutes) / 60 + parseFloat(seconds || 0) / 3600;
  if (m[1] === '-' || /[SW]/i.test(hemi || '')) dec = -dec;
  return dec;
}

/**
 * Full-file scan: delimiter + header + columns in one call.
 * @param {string} text - raw file content
 * @returns {{ delimiter, headers, rows, detection }}
 */
export function analyzeTabularFile(text, hint = '') {
  text = String(text ?? '').replace(/^\uFEFF/, '');
  const delimiter = detectDelimiter(text);
  const allRows = text.split(/\r?\n/).filter(l => l.trim()).map(l => splitLine(l, delimiter));
  const headerIdx = detectHeaderRow(allRows);
  const headers = headerIdx >= 0
    ? allRows[headerIdx]
    : (allRows[0] || []).map((_, i) => `col_${i + 1}`);
  const rows = allRows.slice(headerIdx >= 0 ? headerIdx + 1 : 0);
  const detection = detectColumns(rows, headers, `${hint || ''}\n${String(text || '').slice(0, 4000)}`);
  return { delimiter, headers, rows, detection, headerRow: headerIdx };
}
